import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FileText } from "lucide-react";
import { Spin } from "antd";
import NurseDischargeModal from "./NurseDischargeModal";
import ClinicalSummary from "./ClinicalSummary";
import Diagnosis from "./Diagnosis";
import ClinicalDetails from "./ClinicalDetails";
import TreatmentSummary from "./TreatmentSummary";
import InvestigationSummary from "./InvestigationSummary";
import ConditionAtDischarge from "./ConditionAtDischarge";
import MedicationOnDischarge from "./MedicationOnDischarge";
import FollowUp from "./FollowUp";
import PatientInstructions from "./PatientInstructions";
import DietAdvice from "./DietAdvice";
import CarePlan from "./CarePlan";
import Disposition from "./Disposition";
import DoctorRemarks from "./DoctorRemarks";
import DoctorAuthentication from "./DoctorAuthentication";
import {
  fetchDischargeSummaryAction,
  setDischargeField,
} from "../../../../Redux/actions/dischargeActions";

export default function Discharge({
  patientDetails,
  patientId,
  admissionId,
  admission,
  onSaveSuccess,
}) {
  const dispatch = useDispatch();
  const [isNurseModalOpen, setIsNurseModalOpen] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const dischSummaryId = useSelector(
    (state) => state.discharge?.dischSummaryId
  );

  const resolvedPatientId =
    patientId ||
    patientDetails?.patientId ||
    patientDetails?.uhid ||
    "";

  const resolvedAdmissionId =
    admissionId ||
    admission?.admissionId ||
    admission?.admissionNumber ||
    admission?.admissionNo ||
    patientDetails?.admissionNo ||
    "";

  // Fetch discharge summary initially using admissionId & patientId filter
  useEffect(() => {
    if (!resolvedPatientId && !resolvedAdmissionId) return;

    let isMounted = true;
    const fetchDischargeData = async () => {
      setLoadingSummary(true);
      try {
        const filterPayload = {
          admissionId: resolvedAdmissionId,
          pageNo: 1,
          pageSize: 30,
          patientId: resolvedPatientId,
        };

        await dispatch(
          fetchDischargeSummaryAction(filterPayload, {
            patientId: resolvedPatientId,
            admissionId: resolvedAdmissionId,
          })
        );
      } catch (err) {
        console.warn(
          "Discharge summary fetch error (defaulted to save flow):",
          err
        );
      } finally {
        if (isMounted) {
          setLoadingSummary(false);
        }
      }
    };

    fetchDischargeData();

    return () => {
      isMounted = false;
    };
  }, [resolvedPatientId, resolvedAdmissionId, dispatch]);

  return (
    <div className="bg-gray-50/50 rounded-xl text-[#374151] p-4 sm:p-6 space-y-6 relative">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {dischSummaryId ? (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              Mode: Update Discharge Summary (ID: {dischSummaryId})
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
              Mode: New Discharge Summary (Save Flow)
            </span>
          )}
        </div>

        <button
          onClick={() => setIsNurseModalOpen(true)}
          className="flex items-center gap-2 bg-[#008b8b] hover:bg-[#007a7a] text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm text-sm cursor-pointer"
        >
          <FileText className="w-4 h-4" />
          Nurse Discharge Notes
        </button>
      </div>

      <NurseDischargeModal
        isOpen={isNurseModalOpen}
        onClose={() => setIsNurseModalOpen(false)}
        admissionNo={resolvedAdmissionId}
        patientId={resolvedPatientId}
      />

      <Spin spinning={loadingSummary} tip="Checking Discharge Summary...">
        <div className="flex flex-col gap-6">
          {/* 1. Clinical Summary */}
          <ClinicalSummary />

          {/* 2. Diagnosis */}
          <Diagnosis />

          {/* 3. Clinical Details */}
          <ClinicalDetails />

          {/* 4. Treatment Summary */}
          <TreatmentSummary />

          {/* 5. Investigation Summary (Read Only) */}
          <InvestigationSummary
            patientDetails={patientDetails}
            patientId={resolvedPatientId}
            admission={admission}
          />

          {/* 6. Condition at Discharge */}
          <ConditionAtDischarge />

          {/* 7. Medication on Discharge */}
          <MedicationOnDischarge />

          {/* 8. Follow-up */}
          <FollowUp />

          {/* 9. Patient Instructions */}
          <PatientInstructions />

          {/* 10. Diet Advice */}
          <DietAdvice />

          {/* 11. Care Plan */}
          <CarePlan />

          {/* 12. Disposition */}
          <Disposition />

          {/* 13. Doctor Remarks */}
          <DoctorRemarks />
        </div>

        {/* 14. Doctor Authentication / Footer */}
        <div className="pt-6">
          <DoctorAuthentication
            onSaveSuccess={onSaveSuccess}
            patientDetails={patientDetails}
            patientId={resolvedPatientId}
            admission={admission}
            admissionId={resolvedAdmissionId}
          />
        </div>
      </Spin>
    </div>
  );
}
